import type {
  CreationArtifactEdgeV1,
  CreationArtifactEligibilityV1,
  CreationArtifactLifecycleV1,
  CreationArtifactProvenanceV1,
  CreationArtifactUsageV1,
  CreationRunMomentV1,
} from '@djgg/creations';

/** Presentation state is deliberately independent of the build state machine. */
export const SHOW_CHAPTERS = ['Discover', 'Plan', 'Build', 'Check', 'Ready'] as const;
export type ShowChapter = typeof SHOW_CHAPTERS[number];

export type ShowArtifactKind = 'discovery' | 'inspiration' | 'image' | 'animation' | 'audio' | 'capture' | 'video' | 'bonus';
export type ShowArtifactCategory = 'identity' | 'world' | 'characters' | 'gameplay' | 'sound' | 'proof' | 'extras';
export type ShowArtifactOrigin = 'intent' | 'reference' | 'existing' | 'current-run';
export type ShowArtifactState = 'current' | 'selected' | 'recommended' | 'unused' | 'unavailable';
export type ShowBeatKind = 'identity' | 'world-cast' | 'gameplay-hook' | 'montage' | 'playable-proof';
export type CreationRunMoment = CreationRunMomentV1;

interface ShowArtifactBase {
  id: string;
  title: string;
  summary: string;
  /** Variants in one group share a beat without losing their own identity. */
  group?: string;
  url?: string;
  /** Optional still paired with a sealed gameplay film. */
  posterUrl?: string;
  label?: string;
  unavailable?: boolean;
  /** A real output of this game. Only these appear in Collection; the film also screens ideas and Deej. */
  gameAsset?: boolean;
  /** Durable text/structured artifacts can be inspected without a media URL. */
  openable?: boolean;
  /** Creation-registry semantics used by arrival-driven presentation recipes. */
  durableKind?: import('@djgg/creations').CreationArtifactKindV1;
  arrivalSeq?: number;
  selected?: boolean;
  strip?: import('@djgg/creations').CreationFilmStripV1;
  /** Optional semantic metadata. Adapters can add this as publication data becomes richer. */
  category?: ShowArtifactCategory;
  origin?: ShowArtifactOrigin;
  state?: ShowArtifactState;
  beat?: ShowBeatKind;
  version?: number;
  /** Durable registry detail. Legacy version artifacts may omit these fields. */
  eligibility?: CreationArtifactEligibilityV1;
  lifecycle?: CreationArtifactLifecycleV1;
  provenance?: CreationArtifactProvenanceV1;
  quality?: { status: 'pending' | 'accepted' | 'rejected'; reason?: string };
  safety?: { status: 'pending' | 'accepted' | 'rejected'; reason?: string };
  arrivedAt?: number;
  intrinsicWidth?: number;
  intrinsicHeight?: number;
  contentType?: string;
  lineage?: {
    parents: Array<Pick<CreationArtifactEdgeV1, 'parentArtifactId' | 'relation'>>;
    children: Array<Pick<CreationArtifactEdgeV1, 'childArtifactId' | 'relation'>>;
  };
  usages?: CreationArtifactUsageV1[];
}

/** External reference imagery is a closed presentation kind: it always keeps
 * first-party display bytes, reference provenance and decoded intrinsic bounds.
 * Other artifacts cannot accidentally opt into those semantics. */
export type ShowArtifact =
  | (ShowArtifactBase & {
      kind: 'inspiration';
      url: string;
      origin: 'reference';
      intrinsicWidth: number;
      intrinsicHeight: number;
    })
  | (ShowArtifactBase & {
      kind: Exclude<ShowArtifactKind, 'inspiration'>;
      intrinsicWidth?: never;
      intrinsicHeight?: never;
    });

export interface ShowScene {
  id: string;
  kind: ShowBeatKind;
  artifacts: ShowArtifact[];
}

export function shortCopy(value: string, limit = 140): string {
  const clean = value.replace(/\s+/g, ' ').trim();
  if (clean.length <= limit) return clean;
  return `${clean.slice(0, limit - 1).replace(/\s+\S*$/, '')}…`;
}

/** Accepted openable inventory. A failed preview never erases its durable item. */
export function collectionArtifacts(artifacts: readonly ShowArtifact[]): ShowArtifact[] {
  return [...new Map(artifacts
    .filter(item => (item.openable || item.gameAsset || (item.origin === 'reference' && !!item.url))
      && item.lifecycle !== 'candidate' && item.lifecycle !== 'rejected'
      && item.quality?.status !== 'rejected' && item.safety?.status !== 'rejected')
    .map(item => [item.id, item])).values()];
}

export const COLLECTION_SECTION_ORDER = ['making', 'inspiration', 'in-game', 'proof'] as const;
export type CollectionSection = typeof COLLECTION_SECTION_ORDER[number];

export const COLLECTION_SECTION_COPY: Record<CollectionSection, string> = {
  making: 'Making',
  inspiration: 'Inspiration',
  'in-game': 'In game',
  proof: 'Proof',
};

export function activeGameUsages(artifact: ShowArtifact, activeVersion: number): CreationArtifactUsageV1[] {
  if (activeVersion < 1) return [];
  return (artifact.usages ?? []).filter(usage => usage.version === activeVersion
    && (usage.kind === 'direct' || usage.kind === 'transformed' || usage.kind === 'secondary'));
}

/** One artifact can truthfully appear in several views without being duplicated in the headline count. */
export function collectionSections(
  artifacts: readonly ShowArtifact[],
  activeVersion: number,
): Record<CollectionSection, ShowArtifact[]> {
  const accepted = collectionArtifacts(artifacts);
  const inspiration = accepted.filter(item => item.eligibility === 'inspiration-only' || item.origin === 'reference');
  return {
    making: accepted.filter(item => !inspiration.includes(item)),
    inspiration,
    'in-game': accepted.filter(item => activeGameUsages(item, activeVersion).length > 0),
    proof: accepted.filter(item => item.durableKind === 'proof' || item.kind === 'capture' || artifactCategory(item) === 'proof'),
  };
}

function includesAny(value: string, terms: readonly string[]): boolean {
  const normalized = value.toLowerCase();
  return terms.some(term => normalized.includes(term));
}

/** A safe display category when the publishing adapter has not supplied one. */
export function artifactCategory(artifact: ShowArtifact): ShowArtifactCategory {
  if (artifact.category) return artifact.category;
  if (artifact.kind === 'audio') return 'sound';
  if (artifact.kind === 'capture') return 'proof';
  if (artifact.kind === 'bonus') return 'extras';
  if (artifact.kind === 'inspiration') return 'extras';
  const hint = `${artifact.group ?? ''} ${artifact.title}`;
  if (includesAny(hint, ['cover', 'title', 'logo', 'identity'])) return 'identity';
  if (includesAny(hint, ['background', 'world', 'scene', 'environment', 'palette'])) return 'world';
  if (includesAny(hint, ['hero', 'player', 'enemy', 'character', 'sprite', 'avatar'])) return 'characters';
  if (artifact.kind === 'discovery') return 'gameplay';
  return 'extras';
}

function beatFor(artifact: ShowArtifact): ShowBeatKind {
  if (artifact.beat) return artifact.beat;
  switch (artifactCategory(artifact)) {
    case 'identity': return 'identity';
    case 'world':
    case 'characters': return 'world-cast';
    case 'gameplay': return 'gameplay-hook';
    case 'proof': return 'playable-proof';
    default: return 'montage';
  }
}

const BEAT_ORDER: readonly ShowBeatKind[] = ['identity', 'world-cast', 'gameplay-hook', 'montage', 'playable-proof'];

/**
 * Curate a few game-first beats. Every artifact is retained, variants share a
 * beat, and explicit semantic metadata wins over filename heuristics.
 */
export function showScenes(artifacts: readonly ShowArtifact[]): ShowScene[] {
  const unique = [...new Map(artifacts.map(item => [item.id, item])).values()];
  const groups = new Map<string, { kind: ShowBeatKind; items: ShowArtifact[]; first: number }>();
  unique.forEach((artifact, index) => {
    const kind = beatFor(artifact);
    const key = `${kind}:${artifact.group ?? artifact.id}`;
    const existing = groups.get(key);
    if (existing) existing.items.push(artifact);
    else groups.set(key, { kind, items: [artifact], first: index });
  });
  return [...groups.entries()]
    .sort(([, a], [, b]) => BEAT_ORDER.indexOf(a.kind) - BEAT_ORDER.indexOf(b.kind) || a.first - b.first)
    .flatMap(([key, group]) => {
      const scenes: ShowScene[] = [];
      for (let i = 0; i < group.items.length; i += 3) {
        scenes.push({ id: `${key}:${i / 3}`, kind: group.kind, artifacts: group.items.slice(i, i + 3) });
      }
      return scenes;
    });
}

/** Unseen material wins over repeats; array position never identifies a scene. */
export function nextShowScene(scenes: readonly ShowScene[], seen: ReadonlySet<string>, current: string | null): string | null {
  const unseen = scenes.find(scene => scene.artifacts.some(artifact => !seen.has(artifact.id)) && scene.id !== current);
  if (unseen) return unseen.id;
  if (!scenes.length) return null;
  return scenes[(scenes.findIndex(scene => scene.id === current) + 1) % scenes.length]!.id;
}

export function buildShowChapter(phase: string | undefined, ready: boolean): ShowChapter {
  if (ready) return 'Ready';
  return phase && ['gates', 'review', 'quality', 'audit', 'publishing'].includes(phase) ? 'Check' : 'Build';
}
